import React, { useEffect, useState, useRef } from 'react';
import { WiThermometer, WiHumidity } from 'react-icons/wi';
import { FaThermometerHalf, FaTint } from 'react-icons/fa';
import styles from './SensorData.module.css';

const API_URL = '/api/sensordata'; // replace with your actual endpoint

const formatSensorName = (url) => {
    if (!url) return '';
    let name = url.replace(/^https?:\/\//, '');
    name = name.replace(/\.sensor\.us\/?$/, '');
    return name;
};

const SensorData = () => {
    const [sensors, setSensors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const controllerRef = useRef(null);
    const intervalRef = useRef(null);

    const fetchSensors = async () => {
        if (controllerRef.current) controllerRef.current.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        try {
            const res = await fetch(API_URL, { signal: controller.signal });
            if (!res.ok) throw new Error(`Network response was not ok (${res.status})`);
            const data = await res.json();
            setSensors(data);
            setError(null);
        } catch (err) {
            if (err.name !== 'AbortError') setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSensors();
        intervalRef.current = setInterval(fetchSensors, 10000);
        return () => {
            clearInterval(intervalRef.current);
            if (controllerRef.current) controllerRef.current.abort();
        };
    }, []);

    if (loading) return <p className={styles.status}>Loading sensor data...</p>;
    if (error) return <p className={styles.status}>Error: {error}</p>;

    return (
        <>
            <div className={styles.container}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Sensor Name</th>
                        <th><WiThermometer /> Temperature (°F)</th>
                        <th>Heat Index (°F)</th>
                        <th><WiHumidity /> Humidity (%)</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sensors.map((sensor, idx) => (
                        <tr
                            key={idx}
                            className={styles.row}
                            onClick={() => setSelected(sensor)}
                        >
                            <td>
                                <a
                                    href={sensor.sensorName}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={styles.link}
                                >
                                    {formatSensorName(sensor.sensorName)}
                                </a>
                            </td>
                            <td><strong>{sensor.temperature}</strong></td>
                            <td><strong>{sensor.heatindex}</strong></td>
                            <td><strong>{sensor.humidity}</strong></td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {selected && (
                <div
                    className={styles.overlay}
                    onClick={() => setSelected(null)}
                >
                    <div
                        className={styles.modal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className={styles.closeBtn}
                            onClick={() => setSelected(null)}
                        >
                            ×
                        </button>
                        <h3>Sensor Details</h3>
                        <p className={styles.modalRow}>
                            <FaThermometerHalf className={styles.modalIcon} style={{ color: '#059e8a' }} />
                            <span className={styles.dhtLabels}>Temperature</span>
                            <span className={styles.value}>{selected.temperature}</span>
                            <sup className={styles.units}>°F</sup>
                        </p>
                        <p className={styles.modalRow}>
                            <FaThermometerHalf className={styles.modalIcon} style={{ color: '#059e8a' }} />
                            <span className={styles.dhtLabels}>Feels like</span>
                            <span className={styles.value}>{selected.heatindex}</span>
                            <sup className={styles.units}>°F</sup>
                        </p>
                        <p className={styles.modalRow}>
                            <FaTint className={styles.modalIcon} style={{ color: '#00add6' }} />
                            <span className={styles.dhtLabels}>Humidity</span>
                            <span className={styles.value}>{selected.humidity}</span>
                            <sup className={styles.units}>%</sup>
                        </p>
                        <p>
                            <strong>Full URL: </strong>
                            <a href={selected.sensorName} target="_blank" rel="noreferrer">
                                {selected.sensorName}
                            </a>
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default SensorData;